import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Radio as RadioIcon, Loader2, Check, Info, Clock, PhoneCall, Download, TrendingUp, Mail } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { sr } from "../../lib/sr";

// Read-only, client-facing status of the radio bookings they've approved.
const STATUS_STYLE = {
  DRAFT: { bg: "#F2F4F7", color: "#475467", label: "Sent to Ops", hint: "We received your booking and are confirming rates with the station." },
  CONFIRMED: { bg: "#EFF4FF", color: "#2E6BE6", label: "Rates confirmed", hint: "Station rates are locked in — your flight is scheduled to air." },
  LIVE: { bg: "#E7F7EF", color: "#12805C", label: "On air", hint: "Your spots are airing on the station now." },
  COMPLETED: { bg: "#F4F0FF", color: "#6941C6", label: "Completed", hint: "This flight has finished airing." },
  CANCELLED: { bg: "#FEF3F2", color: "#B42318", label: "Cancelled", hint: "This booking was cancelled." },
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
function money(v, symbol) { return `${symbol || "$"}${fmt(v)}`; }
function symFor(cur) { return cur === "AED" ? "AED " : "$"; }
function timeAgo(iso) {
  if (!iso) return "";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
}

function FlightCard({ flight }) {
  const cur = flight.currency || "USD";
  const symbol = symFor(cur);
  const st = STATUS_STYLE[flight.status] || STATUS_STYLE.DRAFT;
  const rows = flight.stations || [];
  const total = useMemo(() => rows.reduce((a, r) => a + Number(r.unit_price || 0) * Number(r.spots || 0), 0), [rows]);
  const totalSpots = useMemo(() => rows.reduce((a, r) => a + Number(r.spots || 0), 0), [rows]);
  const [dl, setDl] = useState(false);

  const downloadReport = async () => {
    setDl(true);
    try {
      const blob = await sr.flightCallReportPdf(flight.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `call-report-${flight.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not download call report"); }
    finally { setDl(false); }
  };

  return (
    <div className="sr-card" style={{ marginBottom: 14 }} data-testid={`myflight-${flight.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <RadioIcon size={16} style={{ color: "var(--gold)" }} />
            <strong style={{ color: "var(--navy)", fontSize: 15.5 }}>{flight.network}</strong>
            <span className="sr-badge" style={{ background: st.bg, color: st.color }} data-testid={`myflight-status-${flight.id}`}>{st.label}</span>
          </div>
          <div className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>{flight.market} · <strong>{cur}</strong> · created {timeAgo(flight.created_at)}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "var(--navy)" }} data-testid={`myflight-total-${flight.id}`}>{money(total, symbol)}/mo</div>
          <div className="sr-muted" style={{ fontSize: 12 }}>{totalSpots} spots/mo</div>
        </div>
      </div>

      <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
        {rows.map((r) => (
          <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "8px 10px", background: "var(--surface)", borderRadius: 8 }} data-testid={`myflight-station-${flight.id}-${r.key}`}>
            {r.logo && <img src={r.logo} alt={r.name} width={30} height={30} style={{ borderRadius: 6, objectFit: "contain", background: "#fff" }} />}
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13.5 }}>{r.name} <span className="sr-muted" style={{ fontWeight: 400 }}>· {r.delivery}</span></div>
              {Array.isArray(r.dayparts) && r.dayparts.length > 0 && <div className="sr-muted" style={{ fontSize: 11, marginTop: 2 }}>{r.dayparts.join(" · ")}</div>}
            </div>
            <div className="sr-muted" style={{ fontSize: 12 }}>{r.spots} spots · {money(r.unit_price, symbol)}/spot</div>
            <div style={{ width: 90, textAlign: "right", fontWeight: 600, color: "var(--navy)", fontSize: 13 }}>{money(Number(r.unit_price || 0) * Number(r.spots || 0), symbol)}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 12, background: st.bg, borderRadius: 8, padding: "8px 11px" }}>
        <Info size={14} style={{ color: st.color, flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 12.5, color: "var(--navy)" }}>{st.hint}</div>
      </div>

      {flight.call_tracking?.number && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }} data-testid={`myflight-calltracking-${flight.id}`}>
          <span className="sr-badge" style={{ background: "#EEF4FF", color: "var(--blue)", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <PhoneCall size={12} /> Call tracking · {flight.call_tracking.number}
          </span>
          <span className="sr-muted" style={{ fontSize: 12 }} data-testid={`myflight-calls-${flight.id}`}>
            {flight.call_tracking.calls} call{flight.call_tracking.calls === 1 ? "" : "s"}{flight.call_tracking.missed ? ` · ${flight.call_tracking.missed} missed` : ""} attributed
          </span>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={downloadReport} disabled={dl} data-testid={`myflight-report-${flight.id}`} style={{ marginLeft: "auto" }}>
            {dl ? <Loader2 size={12} className="sr-spin" /> : <><Download size={12} /> Call report (PDF)</>}
          </button>
        </div>
      )}
    </div>
  );
}

function BestCallTimes() {
  const [ins, setIns] = useState(null);
  const [digesting, setDigesting] = useState(false);
  useEffect(() => { sr.radioCallInsights().then(setIns).catch(() => setIns({ total: 0, series: [] })); }, []);

  const emailRecap = async () => {
    setDigesting(true);
    try {
      const r = await sr.sendMyCallDigest();
      if (r?.sent) toast.success(`Call recap emailed to ${r.to}`);
      else if (r?.reason === "no_activity") toast.info("No calls last week — nothing to recap yet.");
      else if (r?.reason === "no_recipient_or_mailgun") toast.error("Add an owner email to receive the recap.");
      else toast.info("Recap not sent.");
    } catch { toast.error("Could not send recap"); }
    finally { setDigesting(false); }
  };

  if (!ins || !ins.total) return null;
  const max = Math.max(1, ...ins.series.map((s) => s.calls));
  return (
    <div className="sr-card" style={{ marginBottom: 14 }} data-testid="best-call-times">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <strong style={{ color: "var(--navy)", display: "inline-flex", alignItems: "center", gap: 7 }}>
          <TrendingUp size={16} style={{ color: "var(--gold)" }} /> Best call times
        </strong>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span className="sr-muted" style={{ fontSize: 12.5 }} data-testid="best-call-times-summary">
            {ins.total} call{ins.total === 1 ? "" : "s"} · {ins.answered} answered · {ins.missed} missed
            {ins.best_daypart && <> · best: <strong style={{ color: "var(--navy)" }}>{ins.best_daypart}</strong></>}
          </span>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={emailRecap} disabled={digesting} data-testid="email-call-recap"
            title="Email me last week's call recap">
            {digesting ? <Loader2 size={13} className="sr-spin" /> : <><Mail size={13} /> Email me last week's recap</>}
          </button>
        </div>
      </div>
      <p className="sr-muted" style={{ fontSize: 12, margin: "6px 0 10px" }}>Which dayparts actually ring the phone — buy the spots that drive calls.</p>
      {ins.nudge && (
        <div data-testid="daypart-nudge" style={{ display: "flex", gap: 9, alignItems: "flex-start", background: "#FFF7E8", border: "1px solid #F2D9A6", borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
          <TrendingUp size={15} style={{ color: "#B7791F", flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12.5, color: "var(--navy)" }}><strong>Buy tip:</strong> {ins.nudge.message}</div>
        </div>
      )}
      <div style={{ display: "grid", gap: 7 }}>
        {ins.series.map((s) => (
          <div key={s.daypart} style={{ display: "flex", alignItems: "center", gap: 10 }} data-testid={`daypart-row-${s.daypart}`}>
            <span style={{ width: 150, fontSize: 12.5, color: "var(--navy)", flexShrink: 0 }}>{s.daypart}</span>
            <div style={{ flex: 1, background: "var(--surface)", borderRadius: 6, height: 16, overflow: "hidden" }}>
              <div style={{ width: `${Math.round((s.calls / max) * 100)}%`, height: "100%", background: "var(--blue)", borderRadius: 6 }} />
            </div>
            <span style={{ width: 74, textAlign: "right", fontSize: 12.5, color: "var(--navy)", fontWeight: 600, flexShrink: 0 }}>
              {s.calls}{s.missed ? <span className="sr-muted" style={{ fontWeight: 400 }}> · {s.missed} miss</span> : null}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CallTrends() {
  const [t, setT] = useState(null);
  useEffect(() => { sr.radioCallTrends().then(setT).catch(() => setT({ weeks: [], trend_pct: 0 })); }, []);
  if (!t || !t.weeks || t.weeks.every((w) => !w.calls)) return null;
  const up = t.trend_pct > 0;
  const flat = t.trend_pct === 0;
  return (
    <div className="sr-card" style={{ marginBottom: 14 }} data-testid="call-trends">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <strong style={{ color: "var(--navy)", display: "inline-flex", alignItems: "center", gap: 7 }}>
          <TrendingUp size={16} style={{ color: "var(--blue)" }} /> Call trends
        </strong>
        <span data-testid="call-trends-badge" className="sr-badge" style={{ background: flat ? "#F2F4F7" : up ? "#E7F7EF" : "#FEF3F2", color: flat ? "#475467" : up ? "#12805C" : "#B42318" }}>
          {flat ? "Steady" : `${up ? "▲ up" : "▼ down"} ${Math.abs(t.trend_pct)}%`} vs prior 4 weeks
        </span>
      </div>
      <p className="sr-muted" style={{ fontSize: 12, margin: "6px 0 10px" }}>Week-over-week tracked calls — is radio demand climbing?</p>
      <div style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={t.weeks} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EAECF0" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#667085" }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#667085" }} axisLine={false} tickLine={false} width={34} />
            <Tooltip cursor={{ fill: "rgba(46,107,230,0.06)" }} contentStyle={{ borderRadius: 8, border: "1px solid #EAECF0", fontSize: 12 }} />
            <Bar dataKey="calls" name="Calls" fill="#2E6BE6" radius={[4, 4, 0, 0]} maxBarSize={34} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function ClientRadioFlights() {
  const [data, setData] = useState(null);

  useEffect(() => {
    sr.myRadioFlights().then(setData).catch(() => setData({ flights: [], statuses: [], counts: {} }));
  }, []);

  const flights = data?.flights || [];

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 4px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <div>
          <h1 style={{ display: "inline-flex", alignItems: "center", gap: 8 }} data-testid="myflights-title"><RadioIcon size={22} /> My Radio Flights</h1>
          <p className="sr-muted" style={{ fontSize: 14, marginTop: 2 }}>Live status of every radio booking you've approved. Ops confirms final rates with the stations before airing.</p>
        </div>
        {data && <button className="sr-btn sr-btn-sm" onClick={() => { setData(null); sr.myRadioFlights().then(setData).catch(() => setData({ flights: [] })); }} data-testid="myflights-refresh">Refresh</button>}
      </div>

      {data === null ? (
        <div className="sr-card" style={{ textAlign: "center", padding: 40 }}><Loader2 className="sr-spin" /></div>
      ) : flights.length === 0 ? (
        <div className="sr-card" style={{ textAlign: "center", padding: 44 }} data-testid="myflights-empty">
          <Clock size={26} color="var(--muted)" />
          <h3 style={{ marginTop: 10 }}>No radio bookings yet</h3>
          <p className="sr-muted" style={{ fontSize: 14 }}>When you approve a radio plan from your growth plan, its live status will appear here.</p>
        </div>
      ) : (
        <div data-testid="myflights-list">
          <BestCallTimes />
          <CallTrends />
          {flights.map((f) => <FlightCard key={f.id} flight={f} />)}
          <div className="sr-muted" style={{ fontSize: 12, marginTop: 6, display: "inline-flex", alignItems: "flex-start", gap: 6 }}>
            <Check size={13} style={{ marginTop: 1, color: "var(--gold)" }} /> Dubai bookings are shown in dirhams (AED); Dallas–Fort Worth in USD.
          </div>
        </div>
      )}
    </div>
  );
}
